import { useCallback, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  DataTableGroupByDropdown,
  Dialog,
  DialogPopup,
  DialogTitle,
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  Icon,
  Text,
  useIsMobile,
  type EntitySelection,
  type BulkAction,
} from "@salestrak/ui";

import { PanelOverlay } from "@salestrak/ui";
import { CustomFieldsTable } from "#app/components/custom-fields/custom-fields-table";
import {
  CustomFieldFormDialog,
  type CustomFieldFormMode,
} from "#app/components/custom-fields/custom-field-form-dialog";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { CUSTOM_FIELD_LIST } from "#app/lib/filters/list-configs";
import type { CustomFieldRow, CustomFieldType } from "#app/lib/data";
import { useFieldTypeSelectorConfig } from "#app/lib/hooks/use-field-type-order";
import { useCustomFieldMutations } from "#app/lib/hooks/use-custom-field-mutations";
import { settleAll } from "#app/lib/utils/settle-all";
import { MANAGE_DIALOG_SURFACE } from "#app/lib/constants/manage-dialog";

const GROUP_BY_LABELS = {
  none: "None",
  type: "Type",
} as const;

// The shared manage-dialog surface plus a zeroed viewport inset, for a body that
// runs flush to the popup edge. The surface carries its own width, height and
// `max-w-none`, so it replaces `size="full"` rather than sitting beside it —
// that size has no width cap, which left this dialog and the integrations hub
// opening wider than every other workspace-config surface on a large monitor.
const FULLSCREEN_POPUP = `${MANAGE_DIALOG_SURFACE} [&_[data-slot=dialog-viewport]]:p-0`;

// ---------------------------------------------------------------------------
// Inner content — only mounts when the dialog is open
// ---------------------------------------------------------------------------

function CustomFieldsManageContent() {
  const isMobile = useIsMobile();
  const [groupByField, setGroupByField] =
    useState<keyof typeof GROUP_BY_LABELS>("type");
  const { searchValue, setSearchValue, sorting, setSorting, where, orderBy } =
    useListQueryState(CUSTOM_FIELD_LIST);
  const fieldTypeSelectorConfig = useFieldTypeSelectorConfig();

  const {
    createCustomField,
    updateCustomField,
    deleteCustomField,
    duplicateCustomField,
    refetchList,
  } = useCustomFieldMutations();

  const handleFieldTypeSelect = useCallback((selections: EntitySelection[]) => {
    const selected = selections[0];
    if (selected) {
      setFormMode({ mode: "create", type: selected.id as CustomFieldType });
    }
  }, []);

  // --- Form dialog state ---
  const [formMode, setFormMode] = useState<CustomFieldFormMode | null>(null);

  const handleEdit = useCallback((field: CustomFieldRow) => {
    setFormMode({ mode: "edit", field });
  }, []);

  const handleCloseForm = useCallback(() => {
    setFormMode(null);
  }, []);

  // --- Delete dialog ---
  const [deleteTarget, setDeleteTarget] = useState<CustomFieldRow | null>(
    null,
  );

  const handleDelete = useCallback((field: CustomFieldRow) => {
    setDeleteTarget(field);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteCustomField(deleteTarget.id);
    await refetchList();
    setFormMode(null);
    setDeleteTarget(null);
  }, [deleteTarget, deleteCustomField, refetchList]);

  // --- Duplicate ---
  const handleDuplicate = useCallback(
    async (field: CustomFieldRow) => {
      await duplicateCustomField(field.id);
    },
    [duplicateCustomField],
  );

  // --- Bulk actions ---
  const bulkActions = useMemo<BulkAction<CustomFieldRow>[]>(
    () => [
      {
        id: "delete",
        type: "confirm",
        label: "Delete",
        icon: "trash-2",
        destructive: true,
        confirmTitle: (rows) =>
          `Delete ${rows.length} custom field${rows.length !== 1 ? "s" : ""}?`,
        confirmDescription: (rows) =>
          `This will permanently delete ${rows.length} custom field${rows.length !== 1 ? "s" : ""}. They will no longer be available in any issues or forms. This cannot be undone.`,
        confirmLabel: "Delete fields",
        onAction: async (selected) => {
          await settleAll(selected, (f) => deleteCustomField(f.id));
          await refetchList();
        },
      },
    ],
    [deleteCustomField, refetchList],
  );

  return (
    // PanelOverlay anchors to this container, and a plain Box is
    // `position: static`, so the relative position is explicit.
    <Box direction="column" fill="flex" overflow="hidden" position="relative">
      <Box direction="column" fill="flex" overflow="hidden">
        <CustomFieldsTable
          where={where ?? {}}
          orderBy={orderBy}
          groupByField={groupByField}
          sorting={sorting}
          onSortingChange={setSorting}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          bulkActions={bulkActions}
          toolbarLeft={
            <DataTableGroupByDropdown
              groupByField={groupByField}
              setGroupByField={(value) =>
                setGroupByField(value as keyof typeof GROUP_BY_LABELS)
              }
              groupByLabels={GROUP_BY_LABELS}
            />
          }
          toolbarActions={
            <EntitySelector
              config={fieldTypeSelectorConfig}
              value={[]}
              onValueChange={handleFieldTypeSelect}
            >
              <EntitySelectorTrigger
                render={
                  <Button size={isMobile ? "icon-sm" : "sm"}>
                    <Icon name="plus" />
                    {!isMobile && <Text>Create field</Text>}
                  </Button>
                }
              />
              <EntitySelectorContent size="sm" align="end">
                <EntitySelectorInput />
                <EntitySelectorPool value="field-types" />
                <EntitySelectorEmpty>
                  No matching field types
                </EntitySelectorEmpty>
              </EntitySelectorContent>
            </EntitySelector>
          }
        />
      </Box>

      {/* Create / Edit sidebar panel — absolute overlay on right edge, full dialog height */}
      {formMode ? (
        <PanelOverlay onDismiss={handleCloseForm}>
          <CustomFieldFormDialog
            formMode={formMode}
            onClose={handleCloseForm}
            onCreate={createCustomField}
            onUpdate={updateCustomField}
            onDelete={handleDelete}
          />
        </PanelOverlay>
      ) : null}

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the {deleteTarget?.name} field? It
              will no longer be available to use in any issues or forms. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete field
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Dialog shell — open/close controlled by props from the provider
// ---------------------------------------------------------------------------

export function CustomFieldsManageDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) onClose();
  };

  // One Dialog at every width, bottom-stuck on mobile.
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPopup className={FULLSCREEN_POPUP}>
        <Box paddingX="md" paddingY="sm">
          <DialogTitle>Custom fields</DialogTitle>
        </Box>
        <CustomFieldsManageContent />
      </DialogPopup>
    </Dialog>
  );
}
